import { Fragment, useState, useEffect, useRef, useMemo } from 'react';
import { Menu, Popover, Transition } from '@headlessui/react';
import { Bars3Icon, BellIcon, XMarkIcon, DocumentIcon, CheckCircleIcon, DocumentTextIcon, PlusIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import DocumentUploader, { addOwnerButtonRef } from './documentTracker';
import FormEditor from './FormEditor';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, query, where, getDocs, onSnapshot, doc, updateDoc, limit, orderBy, getDoc, setDoc, addDoc } from 'firebase/firestore';
import ChatBot, { documentRequirements } from './chat';
import logo from '../images/images.png';
import 'intro.js/introjs.css';
import introJs from 'intro.js';
import TermSheetViewer from './TermSheetViewer';
import ApplicationView from './ApplicationView';
import DealRoom from './DealRoom';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { PiCertificateFill } from 'react-icons/pi';
import Prequalification from './Prequalification';
import LenderMatch from './LenderMatch';
import FinancialMetricsDisplayCustomer from './FinancialMetricsDisplayCustomer';
import BorrowerIntakeDocuments from './BorrowerIntakeDocuments';
import BusinessDocsCard from './BusinessDocsCard';
import CapitalReadinessCard from './CapitalReadinessCard';
import EligibilityQuestionnaire from './EligibilityQuestionnaire';
import DealBoxCard from './DealBoxCard';
import { borrowerIntakeDocuments } from '../../config/intakeDocumentRequirements';
import { useNavigate, Link } from 'react-router-dom';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

// Personal doc labels must match DocumentUploader (documentTracker.js) exactly.
// Business doc labels must match BusinessDocsCard's REQUIRED_DOCS / EXTRA_DOCS.
const PERSONAL_DOC_LABELS = new Set([
  'Personal Tax Return - Current Year',
  'Personal Tax Return - Last Year',
  'Personal Tax Return - 2 Years Ago',
  'Personal Financial Statement',
  'Resume',
  'Liquidity Statement - Most Recent Month',
  'Liquidity Statement - Prior Month',
  'Signed Credit Pull Authorization',
  ...borrowerIntakeDocuments.filter(item => item.individual).flatMap(item => item.labels),
]);

const PACKAGING_DOC_BY_LABEL = Object.fromEntries(
  borrowerIntakeDocuments.flatMap(item => item.labels.map(label => [label, { documentName: item.name, individual: item.individual }]))
);

// Doc-type options for the group upload modal
const GROUP_UPLOAD_DOCS = [
  { group: 'Personal', items: [
    { value: 'Personal Tax Return - Current Year', label: 'Personal Tax Return — Current Year' },
    { value: 'Personal Tax Return - Last Year', label: 'Personal Tax Return — Last Year' },
    { value: 'Personal Tax Return - 2 Years Ago', label: 'Personal Tax Return — 2 Years Ago' },
    { value: 'Personal Financial Statement', label: 'Personal Financial Statement (PFS)' },
    { value: 'Resume', label: 'Resume / CV' },
    { value: 'Liquidity Statement - Most Recent Month', label: 'Proof of Liquidity — Most Recent Month' },
    { value: 'Liquidity Statement - Prior Month', label: 'Proof of Liquidity — Prior Month' },
    { value: 'Signed Credit Pull Authorization', label: 'Signed Credit Pull Authorization' },
  ]},
  { group: 'After engagement', items: borrowerIntakeDocuments.flatMap(item => item.labels.map(label => ({ value: label, label }))) },
  { group: 'Business', items: [
    { value: 'Business Tax Return 2025', label: 'Business Tax Return 2025' },
    { value: 'Business Tax Return 2024', label: 'Business Tax Return 2024' },
    { value: 'Business Tax Return 2023', label: 'Business Tax Return 2023' },
    { value: 'Interim P&L 2026 YTD', label: 'Interim P&L 2026 (YTD)' },
    { value: 'Trailing 12 Months P&L', label: 'Trailing 12 Months P&L' },
    { value: 'Balance Sheet', label: 'Balance Sheet' },
    { value: 'CIM', label: 'Confidential Information Memorandum (CIM)' },
    { value: 'LOI', label: 'Letter of Intent (LOI)' },
    { value: 'Debt Schedule', label: 'Debt Schedule' },
    { value: 'AR Aging', label: 'AR Aging' },
    { value: 'AP Aging', label: 'AP Aging' },
    { value: 'Bank Statements', label: 'Bank Statements' },
    { value: 'Real Estate Valuation', label: 'Real Estate Valuation / Appraisal' },
  ]},
];

// Best-effort guess of doc type from filename.
// Personal tax-return year detection uses today's year to map calendar years to
// "Current / Last / 2 Years Ago" labels that DocumentUploader expects.
const guessGroupUploadDocType = (filename) => {
  const n = (filename || '').toLowerCase();
  const yearMatch = n.match(/20(2[0-9])/);
  const year = yearMatch ? Number(`20${yearMatch[1]}`) : null;
  // "Current" personal tax year = most recently filed (last calendar year)
  const currentYear = new Date().getFullYear() - 1;

  if (/p[\s_-]*&?[\s_-]*l|profit[\s_-]*and[\s_-]*loss/.test(n)) {
    if (n.includes('trailing') || n.includes('ttm') || n.includes('t12')) return 'Trailing 12 Months P&L';
    if (year === 2026 || n.includes('ytd') || n.includes('interim')) return 'Interim P&L 2026 YTD';
  }
  if (/personal[\s_-]*tax|1040/.test(n)) {
    if (year != null) {
      if (year >= currentYear) return 'Personal Tax Return - Current Year';
      if (year === currentYear - 1) return 'Personal Tax Return - Last Year';
      if (year === currentYear - 2) return 'Personal Tax Return - 2 Years Ago';
    }
    if (/current/.test(n)) return 'Personal Tax Return - Current Year';
    if (/last/.test(n)) return 'Personal Tax Return - Last Year';
  }
  if (/(business|biz|corp|1120|1065)[\s_-]*tax|tax[\s_-]*return/.test(n) && year != null) {
    return `Business Tax Return ${year}`;
  }
  if (/pfs|personal[\s_-]*financial[\s_-]*statement/.test(n)) return 'Personal Financial Statement';
  if (/resume|cv\b/.test(n)) return 'Resume';
  if (/credit.*(auth|authorization)|authorization.*credit/.test(n)) return 'Signed Credit Pull Authorization';
  if (/driver.?s.*license|passport|government.*id/.test(n)) return 'Government-Issued ID';
  if (/personal.*debt.*schedule/.test(n)) return 'Personal Debt Schedule';
  if (/source.*fund|fund.*source|transfer.*trail/.test(n)) return 'Source of Funds Documentation';
  if (/gift.*letter/.test(n)) return 'Gift Letter and Transfer Evidence';
  if (/divorce.*decree|support.*document/.test(n)) return 'Divorce Decree or Support Documentation';
  if (/paystub|pay.?stub|\bw-?2\b/.test(n)) return 'W-2 or Recent Paystubs';
  if (/ein.*(letter|confirmation)/.test(n)) return 'EIN Confirmation';
  if (/operating.*agreement/.test(n)) return 'Operating Agreement';
  if (/articles.*organization/.test(n)) return 'Articles of Organization';
  if (/(bank|brokerage|liquidity).*statement/.test(n)) return 'Liquidity Statement - Most Recent Month';
  if (/loi|letter[\s_-]*of[\s_-]*intent/.test(n)) return 'LOI';
  if (/balance[\s_-]*sheet/.test(n)) return 'Balance Sheet';
  if (/cim|memorandum/.test(n)) return 'CIM';
  if (/debt[\s_-]*schedule/.test(n)) return 'Debt Schedule';
  if (/\bar[\s_-]*aging\b/.test(n)) return 'AR Aging';
  if (/\bap[\s_-]*aging\b/.test(n)) return 'AP Aging';
  if (/bank[\s_-]*statement/.test(n)) return 'Bank Statements';
  if (/appraisal|valuation/.test(n)) return 'Real Estate Valuation';
  return '';
};

const user = {
  name: 'Tom Cook',
  email: 'tom@example.com',
  imageUrl:
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
};

const navigation = [
  { id: 'currentTask', name: 'Document Upload' },
  { id: 'termSheets', name: 'Term Sheets' },
];

const userNavigation = [
  { name: 'Your Profile', href: '#' },
  { name: 'Settings', href: '#' },
  { name: 'Sign out', href: '#' },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Example() {
  const [selectedItem, setSelectedItem] = useState('currentTask');
  const [currentUser, setCurrentUser] = useState(null);
  const [isChatbotExpanded, setIsChatbotExpanded] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [tutorialStarted, setTutorialStarted] = useState(false);
  const [currentLoanId, setCurrentLoanId] = useState(null);
  const addOwnerButtonRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loanType, setLoanType] = useState(null);
  const [personalGuarantors, setPersonalGuarantors] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [formData, setFormData] = useState({});
  const [selectedDocument, setSelectedDocument] = useState(null);
  const navigate = useNavigate();
  const documentTrackerRef = useRef(null);
  const [editMode, setEditMode] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showBusinessSelector, setShowBusinessSelector] = useState(true);
  const [prequalifiedListings, setPrequalifiedListings] = useState([]);
  // Group upload state
  const [bulkQueue, setBulkQueue] = useState([]); // [{ file, label, customLabel, status, error }]
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkTargetBusinessIndex, setBulkTargetBusinessIndex] = useState(''); // '' = unassigned, otherwise a number (as string)
  const [docsRefreshKey, setDocsRefreshKey] = useState(0); // bumped after bulk upload so DocumentUploader re-fetches
  const [listingsSearchQuery, setListingsSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [priceRangeFilter, setPriceRangeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [applicationStep, setApplicationStep] = useState(1);
  const [capitalProfile, setCapitalProfile] = useState({});
  const [isListingLinked, setIsListingLinked] = useState(false);
  const [isFinancialAnalysisExpanded, setIsFinancialAnalysisExpanded] = useState(true);

  // Ref to track current selectedBusiness for use in callbacks (avoids stale closure)
  const selectedBusinessRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    selectedBusinessRef.current = selectedBusiness;
  }, [selectedBusiness]);

  // Filter documents by business - personal docs show for all, business docs filtered by businessId or businessIndex
  const filteredUploadedDocuments = useMemo(() => {
    const currentBusinessIndex = selectedBusiness?.index;
    const currentBusinessId = selectedBusiness?.businessId;
    return uploadedDocuments.filter(doc => {
      // Personal documents (have ownerId) show for all businesses
      if (doc.ownerId) return true;
      // If no business selected, show all docs
      if (currentBusinessIndex === null || currentBusinessIndex === undefined) return true;

      // If business has a businessId, ONLY show documents with that exact businessId
      // This prevents old documents without businessId from showing up
      if (currentBusinessId) {
        return doc.businessId === currentBusinessId;
      }

      // Legacy: business without businessId - only match documents without businessId by index
      if (!currentBusinessId && !doc.businessId) {
        return doc.businessIndex == currentBusinessIndex;
      }

      return false;
    });
  }, [uploadedDocuments, selectedBusiness]);

  const startTutorial = () => {
    const intro = introJs();
    intro.setOptions({
      steps: [
        {
          element: '#personalFilesSection',
          intro: 'Welcome! Start by uploading your personal documents here. You can drag and drop files or click to browse. These documents apply to all your loan applications.',
        },
        {
          element: '#preCheckSection',
          intro: 'This section shows all required personal documents. Upload your tax returns, financial statements, and other personal files for each guarantor.',
        },
        {
          element: '#businessSelectorSection',
          intro: 'This is where you manage your businesses. Each business you\'re looking to acquire or finance will have its own set of documents.',
        },
        {
          element: '#addNewBusinessButton',
          intro: 'Click here to add a new business. This will take you to a form where you can enter the business details and start your loan application.',
        },
      ],
    });
    intro.start();
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          const db = getFirestore();
          const formQuery = query(collection(db, 'loanApplications'), where('userId', '==', currentUser.uid));

          let unsubscribeDocuments = null;

          const unsubscribeForm = onSnapshot(formQuery, (querySnapshot) => {
            if (!querySnapshot.empty) {
              // Get the user's single loanApplication document
              const formDoc = querySnapshot.docs[0];
              const fetchedFormData = formDoc.data();
              const loanId = formDoc.id;

              // Build businesses array from the businesses field or from root level (backwards compatibility)
              let allBusinesses = [];
              if (fetchedFormData.businesses && fetchedFormData.businesses.length > 0) {
                // New format: businesses array
                allBusinesses = fetchedFormData.businesses.map((business, index) => ({
                  ...business,
                  index, // Store the index for editing
                  docId: loanId, // Reference to parent document
                }));
              } else if (fetchedFormData.businessName || fetchedFormData.loanSize) {
                // Old format: business data at root level - treat as single business
                allBusinesses = [{
                  businessName: fetchedFormData.businessName,
                  loanSize: fetchedFormData.loanSize,
                  industry: fetchedFormData.industry,
                  status: fetchedFormData.status,
                  loanapplicationstatus: fetchedFormData.loanapplicationstatus,
                  index: -1, // Special flag for legacy root data
                  docId: loanId,
                }];
              }
              setBusinesses(allBusinesses);

              // Update firstName from root level data
              if (fetchedFormData.firstName) {
                const capitalizedFirstName = fetchedFormData.firstName.charAt(0).toUpperCase() + fetchedFormData.firstName.slice(1);
                setFirstName(capitalizedFirstName);
              }
              setCurrentLoanId(loanId);
              setIsListingLinked(
                fetchedFormData.fromPrequalification === true || Boolean(fetchedFormData.prequalificationId)
              );
              setCapitalProfile({
                capitalNeed: fetchedFormData.capitalNeed || '',
                engagementSigned: fetchedFormData.engagementSigned === true,
                engagementPaid: fetchedFormData.engagementPaid === true,
                borrowerPrequalAmount: fetchedFormData.borrowerPrequalAmount || null,
                eligibilityQuestionnaireComplete: fetchedFormData.eligibilityQuestionnaireComplete === true,
                capitalPackagingComplete: fetchedFormData.capitalPackagingComplete === true,
              });

              // Handle business selection and form data
              // Use ref to get current selectedBusiness (avoids stale closure in onSnapshot)
              const currentSelected = selectedBusinessRef.current;
              const currentSelectedIndex = currentSelected?.index;
              const selectedStillExists = currentSelectedIndex !== null && currentSelectedIndex !== undefined &&
                allBusinesses.some(b => b.index == currentSelectedIndex);

              if (!currentSelected && allBusinesses.length > 0) {
                // Only auto-select first business if NO business is selected at all
                const firstBusiness = allBusinesses[0];
                setSelectedBusiness(firstBusiness);
                setFormData(firstBusiness);
                setLoanType(firstBusiness.loanPurpose);
              } else if (currentSelected && selectedStillExists) {
                // Update selected business with latest data - keep viewing the same business
                const updatedSelected = allBusinesses.find(b => b.index == currentSelectedIndex);
                if (updatedSelected) {
                  setSelectedBusiness(updatedSelected);
                  setFormData(updatedSelected);
                  setLoanType(updatedSelected.loanPurpose);
                }
              } else if (!currentSelected) {
                // No business selected and no businesses exist - use root data
                setFormData(fetchedFormData);
                setLoanType(fetchedFormData.loanPurpose);
              }
              // If currentSelected exists but not in array, keep current selection/formData unchanged

              if (fetchedFormData.personalGuarantors) {
                setPersonalGuarantors(fetchedFormData.personalGuarantors);
              }

              // Read applicationStep from Firebase (default to 1 if not set)
              if (fetchedFormData.applicationStep) {
                setApplicationStep(fetchedFormData.applicationStep);
              }

              // Clean up previous document listener if it exists
              if (unsubscribeDocuments) {
                unsubscribeDocuments();
                unsubscribeDocuments = null;
              }

              // Set up real-time document updates when we have a loan ID
              if (loanId) {
                // Note: Removed orderBy to avoid needing composite index - sorting done client-side
                const documentsQuery = query(
                  collection(db, 'documentMetadata'),
                  where('userId', '==', currentUser.uid)
                );

                unsubscribeDocuments = onSnapshot(documentsQuery, async (snapshot) => {
                  // Batch OCR queries to reduce reads
                  const documentIds = snapshot.docs.map(doc => doc.id);
                  
                  // Fetch OCR metadata in batches (Firestore 'in' queries limited to 10 items)
                  const ocrBatches = [];
                  for (let i = 0; i < documentIds.length; i += 10) {
                    const batch = documentIds.slice(i, i + 10);
                    const ocrMetadataQuery = query(
                      collection(db, 'OCRMetadata'),
                      where('userId', '==', currentUser.uid),
                      where('documentId', 'in', batch)
                    );
                    ocrBatches.push(getDocs(ocrMetadataQuery));
                  }
                  
                  let ocrDataMap = {};
                  try {
                    const ocrSnapshots = await Promise.all(ocrBatches);
                    ocrSnapshots.forEach(snapshot => {
                      snapshot.forEach(doc => {
                        const data = doc.data();
                        if (data.documentId) {
                          ocrDataMap[data.documentId] = data.ocrResult || data.structuredData || '';
                        }
                      });
                    });
                  } catch (error) {
                    console.error('Error fetching OCR metadata:', error);
                  }

                  const documents = snapshot.docs.map((doc) => {
                    const documentData = doc.data();
                    return {
                      id: doc.id,
                      ...documentData,
                      ocrResult: ocrDataMap[doc.id] || '',
                    };
                  });

                  // Sort by uploadedAt descending (most recent first)
                  documents.sort((a, b) => {
                    const dateA = a.uploadedAt ? new Date(a.uploadedAt) : new Date(0);
                    const dateB = b.uploadedAt ? new Date(b.uploadedAt) : new Date(0);
                    return dateB - dateA;
                  });

                  setUploadedDocuments(documents);
                });
              }
            }
          });

          return () => {
            if (unsubscribeDocuments) {
              unsubscribeDocuments();
            }
            unsubscribeForm();
          };
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };

    fetchUserData();
  }, [currentUser]);

  // Tutorial useEffect - triggers when user first signs up
  useEffect(() => {
    const checkAndStartTutorial = async () => {
      if (currentUser && showBusinessSelector && !tutorialStarted) {
        try {
          const db = getFirestore();
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (!userDoc.exists()) {
            // Create user doc if it doesn't exist
            await setDoc(userDocRef, { hasSeenTutorial: false });
          }

          const userData = userDoc.exists() ? userDoc.data() : { hasSeenTutorial: false };

          if (!userData.hasSeenTutorial) {
            // Small delay to ensure DOM elements are rendered
            setTimeout(() => {
              const personalFilesSection = document.getElementById('personalFilesSection');
              if (personalFilesSection) {
                startTutorial();
                setTutorialStarted(true);
                // Mark tutorial as seen
                updateDoc(userDocRef, { hasSeenTutorial: true });
              }
            }, 500);
          }
        } catch (error) {
          console.error('Error checking tutorial status:', error);
        }
      }
    };

    checkAndStartTutorial();
  }, [currentUser, showBusinessSelector, tutorialStarted]);

  // Fetch all prequalified listings from brokers
  useEffect(() => {
    const fetchPrequalifiedListings = async () => {
      try {
        const db = getFirestore();
        const prequalQuery = query(
          collection(db, 'prequalificationApplications'),
          where('status', '==', 'prequalified')
        );
        const querySnapshot = await getDocs(prequalQuery);
        const listings = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Fetch broker names for each listing
        const brokerIds = [...new Set(listings.map(l => l.brokerId).filter(Boolean))];
        const brokerNames = {};
        for (const brokerId of brokerIds) {
          try {
            const brokerDoc = await getDoc(doc(db, 'brokers', brokerId));
            if (brokerDoc.exists()) {
              const brokerData = brokerDoc.data();
              // Prefer firstName + lastName, fallback to companyName, then email
              const fullName = brokerData.firstName && brokerData.lastName
                ? `${brokerData.firstName} ${brokerData.lastName}`
                : null;
              brokerNames[brokerId] = fullName || brokerData.companyName || brokerData.name || brokerData.email || 'Unknown Broker';
            }
          } catch (err) {
            console.error('Error fetching broker:', brokerId, err);
          }
        }

        // Add broker name to each listing
        const listingsWithBrokerNames = listings.map(listing => ({
          ...listing,
          brokerName: listing.brokerId ? brokerNames[listing.brokerId] : null
        }));

        console.log('Fetched prequalified listings:', listingsWithBrokerNames);
        setPrequalifiedListings(listingsWithBrokerNames);
      } catch (error) {
        console.error('Error fetching prequalified listings:', error);
      }
    };

    fetchPrequalifiedListings();
  }, []);

  // Function to add a prequalified listing to user's deals
  const addListingToDeal = async (listing) => {
    if (!currentUser) {
      console.error('No user logged in');
      return;
    }

    try {
      const db = getFirestore();
      const loanAppsQuery = query(
        collection(db, 'loanApplications'),
        where('userId', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(loanAppsQuery);

      // Check if already added first
      if (!querySnapshot.empty) {
        const loanAppDoc = querySnapshot.docs[0];
        const docData = loanAppDoc.data();
        const businesses = docData.businesses || [];
        const alreadyAdded = businesses.some(b => b.prequalificationId === listing.id);
        if (alreadyAdded) {
          alert('This listing has already been added to your deals!');
          return;
        }
      }

      // Generate unique business ID
      const businessId = `biz_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const newBusiness = {
        businessId, // Unique identifier for this business
        businessName: listing.businessName || 'Unnamed Business',
        businessState: listing.businessState || '',
        businessAge: listing.businessAge || '',
        businessSummary: listing.businessSummary || listing.businessDescription || '',
        loanSize: listing.purchasePrice || listing.loanSize || '',
        purchaseType: listing.purchaseType || 'Asset',
        loanPurpose: 'Acquisition',
        fromPrequalification: true,
        prequalified: true, // This makes the card show "Prequalified"
        prequalificationId: listing.id,
        prequalificationData: {
          amount: listing.prequalifiedAmount || 0,
          purchasePrice: listing.purchasePrice || '',
          realEstateIncluded: listing.realEstateIncluded || false,
          realEstateValue: listing.realEstateValue || '',
          brokerId: listing.brokerId || listing.userId || null
        },
        createdAt: new Date(),
        lastUpdated: new Date(),
        loanapplicationstatus: 'in progress'
      };

      let loanAppId;
      let businessIndex;

      if (!querySnapshot.empty) {
        // User has existing loan application - add to businesses array
        const loanAppDoc = querySnapshot.docs[0];
        const docData = loanAppDoc.data();
        const businesses = docData.businesses || [];
        businessIndex = businesses.length;
        businesses.push(newBusiness);
        await updateDoc(doc(db, 'loanApplications', loanAppDoc.id), { businesses });
        loanAppId = loanAppDoc.id;
      } else {
        // Create new loan application document
        const newDocRef = await addDoc(collection(db, 'loanApplications'), {
          userId: currentUser.uid,
          createdAt: new Date(),
          businesses: [newBusiness],
          ...newBusiness,
          loanapplicationstatus: 'in progress'
        });
        loanAppId = newDocRef.id;
        businessIndex = 0;
      }

      // Copy documents from prequalification
      // Map broker labels to customer portal labels
      const labelMapping = {
        '2024 Business Tax Return': 'Business Tax Return - Current Year',
        '2023 Business Tax Return': 'Business Tax Return - Last Year',
        '2022 Business Tax Return': 'Business Tax Return - 2 Years Ago',
        '2025 YTD P&L': 'Interim P&L',
        '2024 YTD P&L': 'Interim P&L',
        '2026 YTD P&L': 'Interim P&L',
        '2025 YTD P+L': 'Interim P&L',
        '2024 YTD P+L': 'Interim P&L',
        '2026 YTD P+L': 'Interim P&L',
        '2025 YTD Balance Sheet': 'Balance Sheet',
        '2024 YTD Balance Sheet': 'Balance Sheet',
        '2026 YTD Balance Sheet': 'Balance Sheet',
        'CIM Document': 'CIM',
      };

      const mapLabel = (brokerLabel) => {
        if (!brokerLabel) return brokerLabel;

        // Direct match first
        if (labelMapping[brokerLabel]) return labelMapping[brokerLabel];

        // Try replacing P+L with P&L
        const altLabel = brokerLabel.replace('P+L', 'P&L');
        if (labelMapping[altLabel]) return labelMapping[altLabel];

        // Try replacing P&L with P+L
        const altLabel2 = brokerLabel.replace('P&L', 'P+L');
        if (labelMapping[altLabel2]) return labelMapping[altLabel2];

        const lower = brokerLabel.toLowerCase();
        // Check for any P&L, P+L, PnL, profit/loss variations
        if (lower.includes('p&l') || lower.includes('p+l') || lower.includes('pnl') || lower.includes('profit') || lower.includes('loss')) {
          return 'Interim P&L';
        }
        // Check for balance sheet variations
        if (lower.includes('balance sheet')) {
          return 'Balance Sheet';
        }
        return brokerLabel;
      };

      let allPrequalDocs = [];
      const seenFileUrls = new Set();

      // Check documents array on the prequal document
      if (listing.documents && listing.documents.length > 0) {
        listing.documents.forEach(doc => {
          if (doc.fileUrl && !seenFileUrls.has(doc.fileUrl)) {
            seenFileUrls.add(doc.fileUrl);
            allPrequalDocs.push(doc);
          }
        });
      }

      // Also check prequalificationMetadata collection
      const prequalMetadataQuery = query(
        collection(db, 'prequalificationMetadata'),
        where('applicationId', '==', listing.id)
      );
      const prequalMetadataSnapshot = await getDocs(prequalMetadataQuery);
      prequalMetadataSnapshot.docs.forEach(docSnap => {
        const docData = docSnap.data();
        if (docData.fileUrl && !seenFileUrls.has(docData.fileUrl)) {
          seenFileUrls.add(docData.fileUrl);
          allPrequalDocs.push(docData);
        }
      });

      console.log('Found', allPrequalDocs.length, 'documents to copy');
      console.log('All prequal docs:', allPrequalDocs.map(d => ({ label: d.label, fileName: d.fileName })));

      // Map broker documentName to borrower documentName
      const mapDocumentName = (brokerDocName, label) => {
        const docNameMapping = {
          'YTD PnL': 'Interim P&L',
          'Business Tax Returns': 'Business Tax Returns',
          'Balance Sheet': 'Balance Sheet',
          'CIM': 'CIM',
        };
        if (docNameMapping[brokerDocName]) return docNameMapping[brokerDocName];
        // Check based on label
        const lowerLabel = (label || '').toLowerCase();
        if (lowerLabel.includes('p&l') || lowerLabel.includes('p+l') || lowerLabel.includes('pnl') || lowerLabel.includes('profit')) {
          return 'Interim P&L';
        }
        if (lowerLabel.includes('balance sheet')) {
          return 'Balance Sheet';
        }
        if (lowerLabel.includes('tax return')) {
          return 'Business Tax Returns';
        }
        return brokerDocName || 'Business Document';
      };

      // Copy each document to documentMetadata
      for (const docData of allPrequalDocs) {
        if (docData.status === 'not_available' || !docData.fileUrl) continue;

        const mappedLabel = mapLabel(docData.label);
        const mappedDocName = mapDocumentName(docData.documentName, docData.label);
        console.log('Mapping:', docData.label, '->', mappedLabel, '| docName:', docData.documentName, '->', mappedDocName);
        await addDoc(collection(db, 'documentMetadata'), {
          fileName: docData.fileName || 'Document',
          fileUrl: docData.fileUrl,
          uploadedAt: docData.uploadedAt || new Date().toISOString(),
          userId: currentUser.uid,
          documentName: mappedDocName,
          loanApplicationId: loanAppId,
          label: mappedLabel,
          businessIndex: businessIndex,
          businessId: businessId, // Unique business identifier
          ownerId: null,
          copiedFromPrequal: true,
          prequalificationId: listing.id,
          originalBrokerId: docData.brokerId || docData.userId || null
        });
      }

      // Update local state and navigate to document upload
      alert('Listing added to your deals! Documents have been copied.');
      setSelectedItem('currentTask');
      setShowBusinessSelector(true);
    } catch (error) {
      console.error('Error adding listing to deals:', error);
      alert('Error adding listing. Please try again.');
    }
  };

  // Function to remove a business from user's deals
  const removeBusinessFromDeals = async (businessIndex, businessName) => {
    if (!currentUser) return;

    const confirmDelete = window.confirm(`Are you sure you want to remove "${businessName || 'this business'}" from your deals?`);
    if (!confirmDelete) return;

    try {
      const db = getFirestore();
      const loanAppsQuery = query(
        collection(db, 'loanApplications'),
        where('userId', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(loanAppsQuery);

      if (!querySnapshot.empty) {
        const loanAppDoc = querySnapshot.docs[0];
        const docData = loanAppDoc.data();
        const businesses = docData.businesses || [];

        // Remove the business at the specified index
        const updatedBusinesses = businesses.filter((_, idx) => idx !== businessIndex);

        await updateDoc(doc(db, 'loanApplications', loanAppDoc.id), {
          businesses: updatedBusinesses
        });

        alert('Business removed from your deals.');
        window.location.reload();
      }
    } catch (error) {
      console.error('Error removing business:', error);
      alert('Error removing business. Please try again.');
    }
  };

  const handleFormDataChange = (formData) => {
    const capitalizedFirstName = formData.firstName.charAt(0).toUpperCase() + formData.firstName.slice(1);
    setFirstName(capitalizedFirstName);
  };

  // Update applicationStep in Firebase
  const updateApplicationStep = async (newStep) => {
    if (!currentUser || !currentLoanId) return;

    // Only update if the new step is higher than current step
    if (newStep <= applicationStep) return;

    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'loanApplications', currentLoanId), {
        applicationStep: newStep
      });
      setApplicationStep(newStep);
      console.log('Updated applicationStep to:', newStep);
    } catch (error) {
      console.error('Error updating applicationStep:', error);
    }
  };

  // Check if all personal documents are uploaded
  const allPersonalDocsUploaded = () => {
    const missingDocs = getNextRequiredDocuments();
    const grouped = groupDocuments(missingDocs);
    return grouped.personal.length === 0;
  };

  // Get current application status for status bar - uses stored applicationStep from Firebase
  const getApplicationStatus = () => {
    if (capitalProfile.capitalPackagingComplete) {
      return { step: 4, title: 'Capital Package Ready', description: 'Your lender and investor materials are prepared for the right acquisition.', color: 'green', icon: 'check' };
    }
    if (capitalProfile.engagementSigned && capitalProfile.engagementPaid) {
      return { step: 3, title: 'Build Your Capital Package', description: 'Upload the stage-two documents so we can line up debt and investor options.', color: 'indigo', icon: 'upload' };
    }
    if (capitalProfile.borrowerPrequalAmount) {
      return { step: 2, title: 'Engagement', description: 'Your prequalification is ready. Complete the engagement to begin full capital packaging.', color: 'purple', icon: 'clock' };
    }
    if (allPersonalDocsUploaded() && capitalProfile.eligibilityQuestionnaireComplete) {
      return { step: 1, title: 'Prequalification Review', description: 'Your minimum package is complete and our team is reviewing it.', color: 'blue', icon: 'clock' };
    }

    const hasAllBusinessDocsUploaded = businesses.some(b => b.allDocumentsUploaded);
    const hasPrequalifiedBusiness = businesses.some(b => b.prequalified);
    const hasTermSheetRequested = businesses.some(b => b.termSheetRequested);

    // Status details based on stored applicationStep
    const statusDetails = {
      1: {
        step: 1,
        title: 'Upload Personal Documents',
        description: 'Complete uploading all personal documents to continue',
        color: 'amber',
        icon: 'upload'
      },
      2: {
        step: 2,
        title: 'Add a Business',
        description: 'Add a new deal or choose from prequalified listings',
        color: 'blue',
        icon: 'plus'
      },
      3: hasAllBusinessDocsUploaded ? {
        step: 3,
        title: 'Awaiting Prequalification',
        description: 'Your documents are under review. We will update you soon.',
        color: 'indigo',
        icon: 'clock'
      } : {
        step: 3,
        title: 'Upload Business Documents',
        description: 'Complete uploading all business documents for your deals',
        color: 'blue',
        icon: 'upload'
      },
      4: {
        step: 4,
        title: 'Prequalified!',
        description: 'Request a term sheet when ready or continue uploading documents',
        color: 'green',
        icon: 'check'
      },
      5: {
        step: 5,
        title: 'Term Sheet Requested',
        description: 'We are reviewing your application and will send a term sheet soon',
        color: 'purple',
        icon: 'clock'
      }
    };

    // Check if we need to advance the step based on current state
    // This handles cases where Firebase step might be behind reality
    let effectiveStep = applicationStep;
    if (hasTermSheetRequested && applicationStep < 5) effectiveStep = 5;
    else if (hasPrequalifiedBusiness && applicationStep < 4) effectiveStep = 4;
    else if (businesses.length > 0 && applicationStep < 3) effectiveStep = 3;

    return statusDetails[effectiveStep] || statusDetails[1];
  };

  const handleNavigationClick = (itemId) => {
    setSelectedItem(itemId);
  };

  const toggleChatbotExpanded = () => {
    setIsChatbotExpanded(!isChatbotExpanded);
  };

  const handleReviewSubmit = () => {
    setSelectedItem('applicationView');
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Filter navigation items based on loan type
  const filteredNavigation = navigation.filter(item => 
    !['workingCapital-nonsba', 'workingCapital'].includes(loanType) || item.id !== 'applicationView'
  );

  // Update getNextRequiredDocuments to properly check personal documents
  const getNextRequiredDocuments = () => {
    if (!formData || !personalGuarantors || !filteredUploadedDocuments) return [];
    
    const missingDocuments = [];
    
    documentRequirements.forEach(requirement => {
      if (requirement.Individual) {
        // For personal documents, check each guarantor
        personalGuarantors.forEach(guarantor => {
          const guarantorName = `${guarantor.firstName} ${guarantor.lastName}`;

          // Special handling for tax returns to check each year
          if (requirement.Name === "Personal Tax Returns") {
            requirement.Labels.forEach(label => {
              const hasDocument = filteredUploadedDocuments.some(doc =>
                doc.documentName === requirement.Name &&
                (doc.ownerId === guarantorName || doc.guarantorId === guarantor.id) &&
                doc.label === label
              );
              if (!hasDocument) {
                missingDocuments.push(`${requirement.Name} - ${label} (${guarantorName})`);
              }
            });
          } else {
            // For other personal documents
            const hasDocument = filteredUploadedDocuments.some(doc =>
              doc.documentName === requirement.Name &&
              (doc.ownerId === guarantorName || doc.guarantorId === guarantor.id)
            );
            if (!hasDocument) {
              missingDocuments.push(`${requirement.Name} (${guarantorName})`);
            }
          }
        });
      } else {
        // For business documents
        requirement.Labels.forEach(label => {
          const hasDocument = filteredUploadedDocuments.some(
            doc => doc.documentName === requirement.Name &&
                  doc.label === label
          );
          if (!hasDocument) {
            missingDocuments.push(`${requirement.Name} - ${label}`);
          }
        });
      }
    });
    
    return missingDocuments;
  };

  // Add this helper function to get status color
  const getStatusColor = (docName) => {
    const baseDocName = docName.split(' (')[0];
    const label = docName.split(' - ')[1];
    
    if (baseDocName === 'Credit Score') {
      const guarantorName = docName.match(/\((.*?)\)/)?.[1];
      return formData?.creditScores?.[guarantorName] ? {
        bg: 'bg-green-50',
        text: 'text-green-700',
        icon: 'text-green-400',
        border: 'border-green-200'
      } : {
        bg: 'bg-yellow-50',
        text: 'text-yellow-700',
        icon: 'text-yellow-400',
        border: 'border-yellow-200'
      };
    }
    
    // Check for specific tax return years
    if (baseDocName.includes('Tax Return')) {
      const hasDocument = filteredUploadedDocuments.some(doc =>
        doc.documentName === baseDocName &&
        doc.label === label
      );

      return hasDocument ? {
        bg: 'bg-green-50',
        text: 'text-green-700',
        icon: 'text-green-400',
        border: 'border-green-200'
      } : {
        bg: 'bg-yellow-50',
        text: 'text-yellow-700',
        icon: 'text-yellow-400',
        border: 'border-yellow-200'
      };
    }

    // For other documents
    return filteredUploadedDocuments.some(doc => doc.documentName === baseDocName) ? {
      bg: 'bg-green-50',
      text: 'text-green-700',
      icon: 'text-green-400',
      border: 'border-green-200'
    } : {
      bg: 'bg-yellow-50',
      text: 'text-yellow-700',
      icon: 'text-yellow-400',
      border: 'border-yellow-200'
    };
  };

  // Helper function to group documents
  const groupDocuments = (documents) => {
    const personal = [];
    const business = [];

    documents.forEach(doc => {
      if (doc.includes('(')) {
        // Documents with guarantor names are personal
        personal.push(doc);
      } else {
        // Documents without guarantor names are business
        business.push(doc);
      }
    });

    return { personal, business };
  };

  // Auto-update applicationStep when conditions change
  useEffect(() => {
    if (!currentLoanId || !currentUser) return;

    const checkAndUpdateStep = async () => {
      // Calculate if personal docs are complete using getNextRequiredDocuments
      const missingDocs = getNextRequiredDocuments();
      const personalMissing = missingDocs.filter(doc => doc.includes('('));
      const personalComplete = personalMissing.length === 0 && personalGuarantors.length > 0;

      const hasBusinesses = businesses.length > 0;
      const hasPrequalifiedBusiness = businesses.some(b => b.prequalified);
      const hasTermSheetRequested = businesses.some(b => b.termSheetRequested);

      let newStep = 1;

      // Determine what step we should be at based on current state
      if (hasTermSheetRequested) {
        newStep = 5;
      } else if (hasPrequalifiedBusiness) {
        newStep = 4;
      } else if (hasBusinesses) {
        newStep = 3;
      } else if (personalComplete) {
        newStep = 2;
      }

      // Update if the step has changed (can go up or down based on current state)
      setApplicationStep(prevStep => {
        if (newStep !== prevStep) {
          // Update Firebase
          const db = getFirestore();
          updateDoc(doc(db, 'loanApplications', currentLoanId), {
            applicationStep: newStep
          }).then(() => {
            console.log('Auto-updated applicationStep to:', newStep);
          }).catch(error => {
            console.error('Error auto-updating applicationStep:', error);
          });
          return newStep;
        }
        return prevStep;
      });
    };

    // Small delay to ensure all data is loaded
    const timeoutId = setTimeout(checkAndUpdateStep, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLoanId, currentUser, uploadedDocuments, personalGuarantors, businesses, formData]);

  // Fix the document click handler to properly navigate
  const handleDocumentClick = (document) => {
    // Navigate to the section first
    setSelectedItem('currentTask');
    
    // Find the matching document
    const docName = document.name.split(' - ')[0];
    const matchingDoc = documentRequirements.find(req => req.Name === docName);
    
    if (matchingDoc) {
      // Small delay to ensure DocumentTracker is mounted
      setTimeout(() => {
        if (documentTrackerRef.current) {
          documentTrackerRef.current.setSelectedDocument(matchingDoc);
          documentTrackerRef.current.handleReviewClick(matchingDoc);
        }
      }, 100);
    }
  };

  const handleLoanApplicationClick = () => {
    if (formData?.loanSize) {
      setEditMode(true);
      setSelectedItem('currentTask');
    } else {
      navigate('/loanform');
    }
  };

  const handleSave = async (updatedFormData) => {
    // Save the form data
    try {
      const db = getFirestore();
      const formRef = doc(db, 'loanApplications', currentLoanId);
      await updateDoc(formRef, updatedFormData);
      setEditMode(false);
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  const handleSelectBusiness = (business) => {
    setSelectedBusiness(business);
    setCurrentLoanId(business.docId); // Use docId (the parent document ID)
    setFormData(business);
    setLoanType(business.loanPurpose);
    if (business.personalGuarantors) {
      setPersonalGuarantors(business.personalGuarantors);
    }
    setShowBusinessSelector(false);
  };

  const handleAddNewBusiness = () => {
    // Lock adding business until personal docs are uploaded (step 1 complete)
    if (applicationStep < 2) {
      alert('Please upload all personal documents before adding a new business.');
      return;
    }
    navigate('/loanform', { state: { isNewBusiness: true } });
  };

  const handleBackToBusinesses = () => {
    setShowBusinessSelector(true);
  };

  // ---- Group upload helpers ----
  const handleBulkFilesPicked = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    if (!currentUser) return;
    // Default the target business: selected business, else first business, else unassigned
    const defaultIdx = selectedBusiness?.index != null
      ? String(selectedBusiness.index)
      : (businesses.find(b => b.businessName)?.index != null
        ? String(businesses.find(b => b.businessName).index)
        : '');
    setBulkTargetBusinessIndex(defaultIdx);
    setBulkQueue(Array.from(fileList).map(file => ({
      file,
      label: guessGroupUploadDocType(file.name),
      customLabel: '',
      status: 'pending',
      error: '',
    })));
  };
  const updateBulkRow = (idx, patch) => {
    setBulkQueue(prev => prev.map((row, i) => i === idx ? { ...row, ...patch } : row));
  };
  const removeBulkRow = (idx) => {
    setBulkQueue(prev => prev.filter((_, i) => i !== idx));
  };
  const bulkUploadOne = async (file, label, guarantorOverride = null) => {
    const storage = getStorage();
    const db = getFirestore();

    const packagingDoc = PACKAGING_DOC_BY_LABEL[label];
    const isPackaging = Boolean(packagingDoc);
    const isPersonal = PERSONAL_DOC_LABELS.has(label);
    // DocumentUploader matches personal docs to a guarantor by name: "First Last"
    const effectiveGuarantor = guarantorOverride || personalGuarantors[0];
    const guarantorId = isPersonal
      ? (effectiveGuarantor?.firstName && effectiveGuarantor?.lastName
          ? `${effectiveGuarantor.firstName} ${effectiveGuarantor.lastName}`.trim()
          : (formData?.firstName && formData?.lastName
            ? `${formData.firstName} ${formData.lastName}`.trim()
            : currentUser?.email || 'Guarantor'))
      : null;

    // Resolve target business from the picker (overrides selectedBusiness) for business docs
    const targetIdx = bulkTargetBusinessIndex !== '' ? Number(bulkTargetBusinessIndex) : null;
    const targetBusiness = (!isPersonal && !isPackaging && targetIdx != null)
      ? businesses.find(b => b.index === targetIdx)
      : null;

    // Personal docs use guarantorId in path; business docs use business-{idx}
    const pathSegment = isPackaging ? 'intake' : isPersonal
      ? guarantorId.replace(/[^a-zA-Z0-9-_]/g, '_')
      : (targetIdx != null ? `business-${targetIdx}` : 'business');
    // For personal docs, documentName is the category (e.g. "Personal Tax Returns"); for business it equals the label
    const documentName = packagingDoc?.documentName || (isPersonal ? (
      label.startsWith('Personal Tax Return') ? 'Personal Tax Returns'
      : label === 'Personal Financial Statement' ? 'Personal Financial Statement'
      : label === 'Resume' ? 'Resumes for Key Team Members'
      : label
    ) : label);

    const loanIdForPath = currentLoanId || 'no-loan';
    const packagingOwnerSegment = isPackaging && packagingDoc.individual ? `/${guarantorId.replace(/[^a-zA-Z0-9-_]/g, '_')}` : '';
    const path = `documents/${currentUser.uid}/${loanIdForPath}/${pathSegment}/${documentName}/${label}${packagingOwnerSegment}/${file.name}`;
    const sRef = storageRef(storage, path);
    await uploadBytes(sRef, file);
    const fileUrl = await getDownloadURL(sRef);
    const metadata = {
      fileName: file.name,
      fileUrl,
      uploadedAt: new Date().toISOString(),
      userId: currentUser.uid,
      documentName,
      label,
      loanApplicationId: currentLoanId || null,
      ownerId: isPersonal ? guarantorId : null,
      businessIndex: (isPersonal || isPackaging) ? null : (targetIdx != null ? targetIdx : null),
      businessId: (isPersonal || isPackaging) ? null : (targetBusiness?.businessId || null),
      uploadedVia: 'group-upload',
      ...(isPackaging ? { documentType: 'intake' } : {}),
    };
    await addDoc(collection(db, 'documentMetadata'), metadata);
  };
  const startBulkUpload = async () => {
    setBulkUploading(true);

    // If any queued file is a personal doc and there's no guarantor on file yet,
    // create one from the user's profile so the "Add New Business" gate unlocks.
    const hasPersonalUpload = bulkQueue.some(row => {
      const lbl = (row.customLabel && row.customLabel.trim()) || row.label;
      return PERSONAL_DOC_LABELS.has(lbl);
    });
    let activeGuarantor = personalGuarantors[0] || null;
    if (hasPersonalUpload && !activeGuarantor && currentLoanId && currentUser) {
      const firstNameGuess = formData?.firstName || (currentUser.displayName?.split(' ')[0]) || 'Guarantor';
      const lastNameGuess = formData?.lastName || (currentUser.displayName?.split(' ').slice(1).join(' ')) || '';
      activeGuarantor = { firstName: firstNameGuess, lastName: lastNameGuess };
      try {
        const db = getFirestore();
        await updateDoc(doc(db, 'loanApplications', currentLoanId), {
          personalGuarantors: [activeGuarantor],
        });
        setPersonalGuarantors([activeGuarantor]);
      } catch (err) {
        console.error('Error auto-creating personal guarantor:', err);
      }
    }

    for (let i = 0; i < bulkQueue.length; i++) {
      const row = bulkQueue[i];
      if (row.status === 'done') continue;
      const label = (row.customLabel && row.customLabel.trim()) || row.label;
      if (!label) { updateBulkRow(i, { status: 'error', error: 'Pick a doc type or give it a custom name' }); continue; }
      updateBulkRow(i, { status: 'uploading', error: '' });
      try {
        await bulkUploadOne(row.file, label, activeGuarantor);
        updateBulkRow(i, { status: 'done' });
      } catch (err) {
        updateBulkRow(i, { status: 'error', error: err.message || 'Upload failed' });
      }
    }
    setBulkUploading(false);
    setDocsRefreshKey(k => k + 1);
  };
  const closeBulkModal = () => {
    if (bulkUploading) return;
    setBulkQueue([]);
    setBulkTargetBusinessIndex('');
  };

  return (
    <div className="min-h-full">
      <Popover as="header" className="bg-blue-900 pb-24">
        {({ open }) => (
          <>
            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
              <div className="relative flex items-center justify-center py-5 lg:justify-between">
                <div className="absolute left-0 flex-shrink-0 lg:static">
                  <a href="#">
                    <span className="sr-only">Your Company</span>
                    <img
                      className="h-8 w-auto"
                      src={logo}
                      alt="Your Company"
                    />
                  </a>
                </div>

                <div className="absolute right-0 top-5 flex-shrink-0 lg:hidden">
                  <Menu as="div" className="relative inline-block text-left">
                    <div>
                      <Menu.Button className="inline-flex w-full justify-center items-center gap-x-1.5 rounded-md bg-blue-800 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-900 focus:ring-white">
                        {navigation.find(item => item.id === selectedItem)?.name}
                        <ChevronDownIcon className="-mr-1 h-5 w-5" aria-hidden="true" />
                      </Menu.Button>
                    </div>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="py-1">
                          {navigation.map((item) => (
                            <Menu.Item key={item.id}>
                              {({ active }) => (
                                <button
                                  onClick={() => handleNavigationClick(item.id)}
                                  className={classNames(
                                    active ? 'bg-blue-100 text-blue-900' : 'text-gray-700',
                                    'block px-4 py-2 text-sm w-full text-left'
                                  )}
                                >
                                  {item.name}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </div>
              <div className="hidden border-t border-white border-opacity-20 py-5 lg:block">
                <div className="grid grid-cols-3 items-center gap-8">
                  <div className="col-span-2">
                    <nav className="flex space-x-4">
                      {filteredNavigation.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleNavigationClick(item.id)}
                          className={classNames(
                            selectedItem === item.id ? 'text-white' : 'text-indigo-100',
                            'rounded-md bg-white bg-opacity-0 px-3 py-2 text-sm font-medium hover:bg-opacity-10'
                          )}
                          aria-current={selectedItem === item.id ? 'page' : undefined}
                        >
                          {item.name}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Popover>
      <main className="-mt-24 pb-8">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
          <h1 className="sr-only">Page title</h1>
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3 lg:gap-8">
            <div className={`grid grid-cols-1 gap-4 ${
              selectedItem === 'termSheets' ||
              selectedItem === 'applicationView' ||
              selectedItem === 'dealRoom' ||
              selectedItem === 'lenderMatch' ||
              selectedItem === 'prequalification' ||
              selectedItem === 'prequalifiedListings' ? 'lg:col-span-3' : 
              isChatbotExpanded ? 'lg:col-span-1' : 'lg:col-span-2'
            }`}>
              <section aria-labelledby="section-1-title">
                <h2 className="sr-only" id="section-1-title">
                  Section title
                </h2>
                <div className="overflow-hidden rounded-lg bg-white shadow">
                  <div className={selectedItem === 'applicationView' ? '' : 'p-6'}>
                    {selectedItem === 'prequalification' ? (
                      <div id="prequalification">
                        <Prequalification
                          currentUser={currentUser}
                          personalGuarantors={personalGuarantors}
                          uploadedDocuments={filteredUploadedDocuments}
                          onFileChange={(file, guarantorId, documentName, label) => {
                            // Implement file upload logic here
                            console.log('File upload:', file, guarantorId, documentName, label);
                          }}
                          loanPurpose={loanType}
                          formData={formData}
                        />
                      </div>
                    ) : selectedItem === 'currentTask' && showBusinessSelector ? (
                      <div className="space-y-8">
                        {/* Group upload CTA */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center justify-between gap-4 flex-wrap">
                          <div className="min-w-0">
                            <h3 className="text-sm font-semibold text-gray-900">Have a bunch of files?</h3>
                            <p className="text-xs text-gray-500 mt-0.5">Upload multiple files or a whole folder — we'll let you tag each one.</p>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <label className="cursor-pointer text-xs font-medium px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
                              Upload files
                              <input type="file" multiple className="hidden"
                                onChange={(e) => { handleBulkFilesPicked(e.target.files); e.target.value = ''; }} />
                            </label>
                            <label className="cursor-pointer text-xs font-medium px-3 py-2 rounded-lg bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                              Upload folder
                              <input type="file" multiple webkitdirectory="" directory="" className="hidden"
                                onChange={(e) => { handleBulkFilesPicked(e.target.files); e.target.value = ''; }} />
                            </label>
                          </div>
                        </div>
                        {/* Application Status Bar */}
                        {(() => {
                          const status = getApplicationStatus();
                          const colorClasses = {
                            amber: 'bg-amber-50 border-amber-200 text-amber-800',
                            blue: 'bg-blue-50 border-blue-200 text-blue-800',
                            green: 'bg-green-50 border-green-200 text-green-800',
                            purple: 'bg-purple-50 border-purple-200 text-purple-800',
                            indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800'
                          };
                          const iconColorClasses = {
                            amber: 'bg-amber-100 text-amber-600',
                            blue: 'bg-blue-100 text-blue-600',
                            green: 'bg-green-100 text-green-600',
                            purple: 'bg-purple-100 text-purple-600',
                            indigo: 'bg-indigo-100 text-indigo-600'
                          };
                          return (
                            <div className={`rounded-xl border-2 p-4 ${colorClasses[status.color]}`}>
                              <div className="flex items-center gap-4">
                                <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${iconColorClasses[status.color]}`}>
                                  {status.icon === 'upload' && (
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                    </svg>
                                  )}
                                  {status.icon === 'plus' && (
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                  )}
                                  {status.icon === 'clock' && (
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  )}
                                  {status.icon === 'check' && (
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${iconColorClasses[status.color]}`}>
                                      Step {status.step}
                                    </span>
                                    <h3 className="text-lg font-bold">{status.title}</h3>
                                  </div>
                                  <p className="text-sm opacity-80 mt-0.5">{status.description}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {currentLoanId && (
                          <div className="space-y-5">
                            <CapitalReadinessCard loanApplicationId={currentLoanId} profile={capitalProfile} onChange={setCapitalProfile} />
                            {!isListingLinked && (
                              <DealBoxCard loanApplicationId={currentLoanId} currentUser={currentUser} />
                            )}
                            <EligibilityQuestionnaire loanApplicationId={currentLoanId} />
                          </div>
                        )}

                        {/* Personal Files Section - Always visible */}
                        <div id="personalFilesSection">
                          <h2 className="text-xl font-semibold text-gray-900 mb-2">Prequalification Documents</h2>
                          <p className="text-sm text-gray-600 mb-4">Upload the minimum personal package we need to make an initial eligibility and capital-capacity decision.</p>
                          <div id="documentUploader">
                            <DocumentUploader
                              ref={documentTrackerRef}
                              onFormDataChange={(data) => {
                                console.log('Form data changed:', data);
                              }}
                              selectedDocument={selectedDocument}
                              setSelectedDocument={setSelectedDocument}
                              currentUser={currentUser}
                              personalGuarantors={personalGuarantors}
                              loanType={loanType}
                              currentLoanId={currentLoanId}
                              showPersonalOnly={true}
                              refreshKey={docsRefreshKey}
                            />
                          </div>
                        </div>

                        {/* Stage 2 packaging documents unlock after engagement is signed and paid. */}
                        {capitalProfile.engagementSigned && capitalProfile.engagementPaid && currentLoanId && (
                          <div className="mt-8">
                            <BorrowerIntakeDocuments
                              currentUser={currentUser}
                              loanApplicationId={currentLoanId}
                              personalGuarantors={personalGuarantors}
                              businessId={null}
                            />
                          </div>
                        )}

                        {/* Divider */}
                        <div className="border-t border-gray-200"></div>

                        {/* Business Selector Section */}
                        <div id="businessSelectorSection">
                          <h2 className="text-xl font-semibold text-gray-900 mb-2">Your Businesses</h2>
                          <p className="text-sm text-gray-600 mb-6">Select a business to view and upload business documents, or add a new one.</p>
                        </div>

                        <div id="businessCardsGrid" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Only show businesses that have actual business data (businessName or loanSize) */}
                          {businesses
                            .filter(business => business.businessName || business.loanSize)
                            .map((business) => (
                            <div
                              key={`business-${business.index}`}
                              className={`relative flex flex-col items-start p-6 bg-white border-2 rounded-xl hover:border-blue-500 hover:shadow-md transition-all text-left group ${
                                business.prequalified ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200'
                              }`}
                            >
                              {/* Delete Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeBusinessFromDeals(business.index, business.businessName);
                                }}
                                className="absolute top-2 left-2 p-1.5 rounded-full bg-gray-100 hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                                title="Remove from deals"
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </button>

                              {/* Prequalified Badge - same style as listings page */}
                              {business.prequalified && (
                                <div className="absolute top-2 right-2">
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
                                    Prequalified
                                  </span>
                                </div>
                              )}

                              {/* Clickable area */}
                              <button
                                onClick={() => handleSelectBusiness(business)}
                                className="w-full text-left"
                              >
                                <div className={`flex items-center justify-center w-12 h-12 rounded-lg mb-4 ${
                                  business.prequalified ? 'bg-emerald-100' : 'bg-blue-100'
                                }`}>
                                  <BuildingOfficeIcon className={`w-6 h-6 ${
                                    business.prequalified ? 'text-emerald-600' : 'text-blue-600'
                                  }`} />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600">
                                  {business.businessName || 'Unnamed Business'}
                                </h3>
                                <p className="text-sm text-gray-500 mt-1">
                                  {business.industry || 'Industry not specified'}
                                </p>
                                {business.loanSize && (
                                  <p className="text-sm text-gray-500 mt-1">
                                    Loan Size: ${Number(business.loanSize).toLocaleString()}
                                  </p>
                                )}
                                {business.prequalified && business.prequalificationData && (
                                  <p className="text-sm text-emerald-600 font-medium mt-1">
                                    Prequalified: ${Number(business.prequalificationData.amount || 0).toLocaleString()}
                                  </p>
                                )}
                                {/* Status tag - don't show "Prequalified" here since badge shows it */}
                                {!business.prequalified && (
                                  <span className={`mt-3 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                    business.status === 'Pre-lender' ? 'bg-yellow-100 text-yellow-800' :
                                    business.status === 'Active' ? 'bg-emerald-100 text-emerald-700' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {business.status || 'Draft'}
                                  </span>
                                )}
                              </button>

                            </div>
                          ))}

                          {/* Add New Business Card */}
                          <button
                            id="addNewBusinessButton"
                            onClick={handleAddNewBusiness}
                            className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl transition-all group min-h-[180px] ${
                              applicationStep >= 2
                                ? 'bg-gray-50 border-gray-300 hover:border-blue-500 hover:bg-blue-50 cursor-pointer'
                                : 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-60'
                            }`}
                          >
                            <div className={`flex items-center justify-center w-12 h-12 rounded-lg mb-4 ${
                              applicationStep >= 2
                                ? 'bg-gray-200 group-hover:bg-blue-100'
                                : 'bg-gray-200'
                            }`}>
                              <PlusIcon className={`w-6 h-6 ${
                                applicationStep >= 2
                                  ? 'text-gray-500 group-hover:text-blue-600'
                                  : 'text-gray-400'
                              }`} />
                            </div>
                            <h3 className={`text-lg font-semibold ${
                              applicationStep >= 2
                                ? 'text-gray-600 group-hover:text-blue-600'
                                : 'text-gray-400'
                            }`}>
                              Add New Business
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                              {applicationStep >= 2
                                ? 'Start a new loan application'
                                : 'Upload personal documents first'}
                            </p>
                          </button>
                        </div>
                      </div>
                    ) : selectedItem === 'currentTask' ? (
                      <>
                        {/* Back to businesses button */}
                        <div className="mb-4 flex items-center justify-between">
                          <button
                            onClick={handleBackToBusinesses}
                            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to all businesses
                          </button>
                          {selectedBusiness && (
                            <div className="flex items-center">
                              <BuildingOfficeIcon className="w-5 h-5 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-700">
                                {selectedBusiness.businessName || 'Unnamed Business'}
                              </span>
                            </div>
                          )}
                        </div>
                        {editMode ? (
                        <FormEditor
                          formData={formData}
                          handleChange={(e) => {
                            const { name, value } = e.target;
                            setFormData(prev => ({
                              ...prev,
                              [name]: value
                            }));
                          }}
                          handleSave={() => handleSave(formData)}
                          handleCancel={handleCancel}
                          handleCapTableChange={(index, field, value) => {
                            const newCapTable = [...formData.capTable];
                            newCapTable[index][field] = value;
                            setFormData(prev => ({
                              ...prev,
                              capTable: newCapTable
                            }));
                          }}
                          handleAddCapTableRow={() => {
                            setFormData(prev => ({
                              ...prev,
                              capTable: [...(prev.capTable || []), { name: '', percentage: '' }]
                            }));
                          }}
                          handleDeleteCapTableRow={(index) => {
                            setFormData(prev => ({
                              ...prev,
                              capTable: prev.capTable.filter((_, i) => i !== index)
                            }));
                          }}
                        />
                      ) : (
                        <>
                          {/* Show DSCR table for prequalified businesses */}
                          {selectedBusiness?.prequalified && currentLoanId && (
                            <div className="mb-8">
                              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                {/* Collapsible Header */}
                                <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100">
                                      <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
                                    </div>
                                    <div>
                                      <h3 className="text-lg font-bold text-gray-900">Financial Analysis</h3>
                                      <p className="text-sm text-gray-500">DSCR and financial metrics for this prequalified deal</p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setIsFinancialAnalysisExpanded(!isFinancialAnalysisExpanded)}
                                    className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none transition-colors"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className={`h-6 w-6 transition-transform ${isFinancialAnalysisExpanded ? 'transform rotate-180' : ''}`}
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                      />
                                    </svg>
                                  </button>
                                </div>
                                {/* Collapsible Content */}
                                {isFinancialAnalysisExpanded && (
                                  <div className="p-6">
                                    <FinancialMetricsDisplayCustomer
                                      loanId={currentLoanId}
                                      onFinancialDataCalculated={(data) => {
                                        console.log('Financial data calculated:', data);
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          <div id="documentUploader">
                            <BusinessDocsCard
                              currentUser={currentUser}
                              loanApplicationId={currentLoanId}
                              businessIndex={selectedBusiness?.index}
                              businessId={selectedBusiness?.businessId}
                              selectedBusiness={selectedBusiness}
                              refreshKey={docsRefreshKey}
                            />
                          </div>

                        </>
                      )}
                      </>
                    ) : selectedItem === 'prequalifiedListings' ? (
                      <div className="bg-white rounded-lg shadow p-6">
                        <div className="mb-6">
                          <h2 className="text-2xl font-bold text-gray-900 mb-2">Prequalified Business Listings</h2>
                          <p className="text-gray-500">Browse businesses that have been prequalified for SBA financing</p>
                        </div>

                        {/* Filter Bar */}
                        <div className="mb-6">
                          <div className="flex flex-wrap items-center gap-3">
                            {/* Location Filter */}
                            <div className="relative">
                              <select
                                value={locationFilter}
                                onChange={(e) => setLocationFilter(e.target.value)}
                                className="appearance-none bg-white border border-gray-300 rounded-lg pl-10 pr-8 py-2.5 text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 cursor-pointer"
                              >
                                <option value="">All Locations</option>
                                {[...new Set(prequalifiedListings.map(l => l.businessState).filter(Boolean))].sort().map(state => (
                                  <option key={state} value={state}>{state}</option>
                                ))}
                              </select>
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                              </div>
                              <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                                <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </div>

                            {/* Industry Filter */}
                            <div className="relative">
                              <select
                                value={industryFilter}
                                onChange={(e) => setIndustryFilter(e.target.value)}
                                className="appearance-none bg-white border border-gray-300 rounded-lg pl-10 pr-8 py-2.5 text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 cursor-pointer"
                              >
                                <option value="">Industries</option>
                                {[...new Set(prequalifiedListings.map(l => l.industry).filter(Boolean))].sort().map(ind => (
                                  <option key={ind} value={ind}>{ind}</option>
                                ))}
                              </select>
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                              </div>
                              <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                                <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </div>

                            {/* Price Range Filter */}
                            <div className="relative">
                              <select
                                value={priceRangeFilter}
                                onChange={(e) => setPriceRangeFilter(e.target.value)}
                                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 pr-8 py-2.5 text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 cursor-pointer"
                              >
                                <option value="">Price Range</option>
                                <option value="0-500000">Under $500K</option>
                                <option value="500000-1000000">$500K - $1M</option>
                                <option value="1000000-2500000">$1M - $2.5M</option>
                                <option value="2500000-5000000">$2.5M - $5M</option>
                                <option value="5000000+">$5M+</option>
                              </select>
                              <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                                <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </div>

                            {/* Search Input */}
                            <div className="relative flex-1 min-w-[200px]">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                              </div>
                              <input
                                type="text"
                                placeholder="Search listings..."
                                value={listingsSearchQuery}
                                onChange={(e) => setListingsSearchQuery(e.target.value)}
                                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>

                            {/* Clear Button */}
                            {(locationFilter || industryFilter || priceRangeFilter || listingsSearchQuery) && (
                              <button
                                onClick={() => {
                                  setLocationFilter('');
                                  setIndustryFilter('');
                                  setPriceRangeFilter('');
                                  setListingsSearchQuery('');
                                }}
                                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Listings Grid */}
                        <div className="relative min-h-[400px]">
                          {/* Overlay when personal docs not uploaded (step 1) */}
                          {applicationStep === 1 && (
                            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                              <div className="text-center p-8 max-w-md">
                                <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                                  <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Upload Personal Documents First</h3>
                                <p className="text-gray-600 mb-4">
                                  Please complete uploading all your personal documents before browsing prequalified listings.
                                </p>
                                <button
                                  onClick={() => setSelectedItem('currentTask')}
                                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
                                >
                                  Go to Document Upload
                                  <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          )}

                        {prequalifiedListings.length === 0 ? (
                          <div className="text-center py-12">
                            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-semibold text-gray-900">No prequalified listings</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Check back later for new prequalified business opportunities.
                            </p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            {prequalifiedListings
                              .filter(listing => {
                                // Location filter
                                if (locationFilter && listing.businessState !== locationFilter) return false;

                                // Industry filter
                                if (industryFilter && listing.industry !== industryFilter) return false;

                                // Price range filter
                                if (priceRangeFilter) {
                                  const price = Number(listing.purchasePrice) || 0;
                                  if (priceRangeFilter === '0-500000' && price >= 500000) return false;
                                  if (priceRangeFilter === '500000-1000000' && (price < 500000 || price >= 1000000)) return false;
                                  if (priceRangeFilter === '1000000-2500000' && (price < 1000000 || price >= 2500000)) return false;
                                  if (priceRangeFilter === '2500000-5000000' && (price < 2500000 || price >= 5000000)) return false;
                                  if (priceRangeFilter === '5000000+' && price < 5000000) return false;
                                }

                                // Search query filter
                                if (listingsSearchQuery) {
                                  const query = listingsSearchQuery.toLowerCase();
                                  return (
                                    (listing.businessName?.toLowerCase() || '').includes(query) ||
                                    (listing.businessState?.toLowerCase() || '').includes(query) ||
                                    (listing.businessSummary?.toLowerCase() || '').includes(query) ||
                                    (listing.businessDescription?.toLowerCase() || '').includes(query) ||
                                    (listing.industry?.toLowerCase() || '').includes(query)
                                  );
                                }
                                return true;
                              })
                              .map((listing) => (
                                <div
                                  key={listing.id}
                                  className="group bg-white border border-gray-200 rounded-2xl hover:border-gray-300 hover:shadow-xl transition-all duration-300 overflow-hidden"
                                >
                                  {/* Card Header */}
                                  <div className="p-5 border-b border-gray-100">
                                    <div className="flex items-start justify-between mb-3">
                                      <div className="flex-1">
                                        <h3 className="text-xl font-bold text-gray-900 group-hover:text-gray-700 transition-colors">
                                          {listing.businessName || 'Unnamed Business'}
                                        </h3>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {/* New badge for listings from past week */}
                                        {listing.createdAt && (() => {
                                          const createdDate = listing.createdAt.toDate ? listing.createdAt.toDate() : new Date(listing.createdAt);
                                          const oneWeekAgo = new Date();
                                          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                                          return createdDate > oneWeekAgo;
                                        })() && (
                                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200 shadow-sm">
                                            <svg className="w-3 h-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd" />
                                            </svg>
                                            New
                                          </span>
                                        )}
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                          <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
                                          Prequalified
                                        </span>
                                      </div>
                                    </div>

                                    {(listing.businessSummary || listing.businessDescription) && (
                                      <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                                        {listing.businessSummary || listing.businessDescription}
                                      </p>
                                    )}
                                  </div>

                                  {/* Financial Info */}
                                  <div className="px-5 py-4 bg-gray-50/50">
                                    <div className="grid grid-cols-2 gap-4">
                                      {listing.prequalifiedAmount && (
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Prequalified For</p>
                                          <p className="mt-1 text-lg font-bold text-emerald-600">
                                            ${Number(listing.prequalifiedAmount).toLocaleString()}
                                          </p>
                                        </div>
                                      )}
                                      {listing.purchasePrice && (
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Asking Price</p>
                                          <p className="mt-1 text-lg font-bold text-gray-900">
                                            ${Number(listing.purchasePrice).toLocaleString()}
                                          </p>
                                        </div>
                                      )}
                                      <div>
                                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Location</p>
                                          <p className="mt-1 text-sm font-semibold text-gray-900">
                                            {listing.businessState || 'N/A'}
                                          </p>
                                        </div>
                                      <div>
                                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Industry</p>
                                          <p className="mt-1 text-sm font-semibold text-gray-900">
                                            {listing.industry || 'N/A'}
                                          </p>
                                        </div>
                                      <div>
                                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">2025 Profit</p>
                                          <p className="mt-1 text-sm font-semibold text-gray-900">
                                            {listing.profit2025 ? `$${Number(listing.profit2025).toLocaleString()}` : 'N/A'}
                                          </p>
                                        </div>
                                      <div>
                                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">2025 DSCR</p>
                                          <p className="mt-1 text-sm font-semibold text-gray-900">
                                            {listing.dscr2025 || 'N/A'}
                                          </p>
                                        </div>
                                    </div>
                                  </div>

                                  {/* Details */}
                                  <div className="px-5 py-4">
                                    <div className="flex flex-wrap gap-2 mb-4">
                                      {listing.businessState && (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-700">
                                          <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                          </svg>
                                          {listing.businessState}
                                        </span>
                                      )}
                                      {listing.industry && (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-purple-50 text-purple-700">
                                          <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                          </svg>
                                          {listing.industry}
                                        </span>
                                      )}
                                      {listing.businessAge && (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-700">
                                          <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                          Established for {listing.businessAge} years
                                        </span>
                                      )}
                                      {listing.realEstateIncluded && (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700">
                                          <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                          </svg>
                                          Real Estate Included
                                        </span>
                                      )}
                                      {listing.licenseNeeded && (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-700">
                                          <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                          </svg>
                                          License Required
                                        </span>
                                      )}
                                      {listing.brokerName && (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-teal-50 text-teal-700">
                                          <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                          </svg>
                                          {listing.brokerName}
                                        </span>
                                      )}
                                    </div>

                                    {/* Additional Stats Row */}
                                    {(listing.realEstateValue || listing.loanSize) && (
                                      <div className="flex items-center justify-between py-3 border-t border-gray-100 mb-4">
                                        {listing.realEstateValue && (
                                          <div className="text-center">
                                            <p className="text-xs text-gray-500">RE Value</p>
                                            <p className="text-sm font-semibold text-gray-900">${Number(listing.realEstateValue).toLocaleString()}</p>
                                          </div>
                                        )}
                                        {listing.loanSize && (
                                          <div className="text-center">
                                            <p className="text-xs text-gray-500">Loan Requested</p>
                                            <p className="text-sm font-semibold text-gray-900">${Number(listing.loanSize).toLocaleString()}</p>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    <button
                                      onClick={() => addListingToDeal(listing)}
                                      className="w-full bg-emerald-600 text-white py-2.5 px-4 rounded-xl font-medium hover:bg-emerald-700 active:bg-emerald-800 transition-colors flex items-center justify-center gap-2"
                                    >
                                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                      </svg>
                                      Add to My Deals
                                    </button>
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                        </div>
                      </div>
                    ) : selectedItem === 'termSheets' ? (
                      <div>
                        {currentLoanId ? (
                          <TermSheetViewer loanId={currentLoanId} />
                        ) : (
                          <p className="mt-1 text-sm text-gray-500">No active loan application found.</p>
                        )}
                      </div>
                    ) : selectedItem === 'applicationView' && !['workingCapital-nonsba', 'workingCapital'].includes(loanType) ? (
                      <div>
                        {selectedBusiness?.fromPrequalification ? (
                          // Show prequalification summary for broker listings
                          <div className="bg-white rounded-lg p-6">
                            <div className="flex items-center gap-3 mb-6">
                              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100">
                                <CheckCircleIcon className="w-6 h-6 text-emerald-600" />
                              </div>
                              <div>
                                <h2 className="text-xl font-bold text-gray-900">Prequalified Listing</h2>
                                <p className="text-sm text-gray-500">This business has been prequalified by a broker</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Business Info */}
                              <div className="space-y-4">
                                <h3 className="font-semibold text-gray-900 border-b pb-2">Business Information</h3>
                                <div>
                                  <p className="text-sm text-gray-500">Business Name</p>
                                  <p className="font-medium text-gray-900">{selectedBusiness.businessName || 'N/A'}</p>
                                </div>
                                {selectedBusiness.businessSummary && (
                                  <div>
                                    <p className="text-sm text-gray-500">Description</p>
                                    <p className="text-gray-900">{selectedBusiness.businessSummary}</p>
                                  </div>
                                )}
                                {selectedBusiness.businessState && (
                                  <div>
                                    <p className="text-sm text-gray-500">Location</p>
                                    <p className="font-medium text-gray-900">{selectedBusiness.businessState}</p>
                                  </div>
                                )}
                                {selectedBusiness.businessAge && (
                                  <div>
                                    <p className="text-sm text-gray-500">Business Age</p>
                                    <p className="font-medium text-gray-900">{selectedBusiness.businessAge}</p>
                                  </div>
                                )}
                              </div>

                              {/* Loan Info */}
                              <div className="space-y-4">
                                <h3 className="font-semibold text-gray-900 border-b pb-2">Loan Details</h3>
                                <div>
                                  <p className="text-sm text-gray-500">Purchase Price</p>
                                  <p className="font-medium text-gray-900 text-lg">
                                    ${Number(selectedBusiness.loanSize || 0).toLocaleString()}
                                  </p>
                                </div>
                                {selectedBusiness.prequalificationData && (
                                  <>
                                    <div>
                                      <p className="text-sm text-gray-500">Prequalified Amount</p>
                                      <p className="font-medium text-emerald-600 text-lg">
                                        ${Number(selectedBusiness.prequalificationData.amount || 0).toLocaleString()}
                                      </p>
                                    </div>
                                    {selectedBusiness.prequalificationData.type && (
                                      <div>
                                        <p className="text-sm text-gray-500">Financing Type</p>
                                        <p className="font-medium text-gray-900">{selectedBusiness.prequalificationData.type}</p>
                                      </div>
                                    )}
                                    {selectedBusiness.prequalificationData.withSalary && (
                                      <div className="flex items-center gap-2">
                                        <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                                        <p className="text-gray-900">Includes $75k Salary</p>
                                      </div>
                                    )}
                                  </>
                                )}
                                {selectedBusiness.purchaseType && (
                                  <div>
                                    <p className="text-sm text-gray-500">Purchase Type</p>
                                    <p className="font-medium text-gray-900">{selectedBusiness.purchaseType}</p>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                              <p className="text-sm text-blue-800">
                                <strong>Next Steps:</strong> Upload your required documents in the Documents tab to complete your application.
                                Once all documents are submitted, we'll match you with lenders.
                              </p>
                            </div>
                          </div>
                        ) : currentLoanId ? (
                          <ApplicationView loanId={currentLoanId} />
                        ) : (
                          <p className="mt-1 text-sm text-gray-500">No active loan application found.</p>
                        )}
                      </div>
                    ) : selectedItem === 'dealRoom' ? (
                      <DealRoom />
                    ) : selectedItem === 'lenderMatch' ? (
                      <LenderMatch />
                    ) : selectedItem === 'prequalification' ? (
                      <Prequalification />
                    ) : null}
                  </div>
                </div>
              </section>
            </div>
            {selectedItem !== 'termSheets' && selectedItem !== 'applicationView' && selectedItem !== 'dealRoom' && selectedItem !== 'lenderMatch' && selectedItem !== 'prequalification' && selectedItem !== 'prequalifiedListings' && (
              <div className={`grid grid-cols-1 gap-4 ${isChatbotExpanded ? 'lg:col-span-2' : 'lg:col-span-1'}`}>
                <section aria-labelledby="section-2-title">
                  <h2 className="sr-only" id="section-2-title">
                    Section title
                  </h2>
                  <div className="overflow-hidden rounded-lg bg-white shadow">
                    <ChatBot
                      firstName={firstName}
                      toggleChatbotExpanded={toggleChatbotExpanded}
                      isChatbotExpanded={isChatbotExpanded}
                    />
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </main>
      
      {/* Add this floating button before the closing div - hide on listings page */}
      {selectedItem !== 'prequalifiedListings' && (
      <div className="fixed bottom-4 right-4 z-50">
        <Menu as="div" className="relative inline-block text-left">
          <Menu.Button className="rounded-full bg-blue-600 p-4 text-white shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <span className="flex items-center">
              What's Next?
              <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
            </span>
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute bottom-full right-0 mb-2 w-96 rounded-md bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center sticky top-0 bg-white">
                  <DocumentIcon className="h-5 w-5 mr-2 text-blue-500" />
                  Next Steps
                </h3>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                  {/* Personal Documents Section */}
                  {groupDocuments(getNextRequiredDocuments()).personal.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2">
                        Personal Documents
                      </h4>
                      <div className="space-y-2">
                        {groupDocuments(getNextRequiredDocuments()).personal.map((doc, index) => {
                          const colors = getStatusColor(doc);
                          const docName = doc.split(' (')[0];
                          const guarantorName = doc.match(/\((.*?)\)/)?.[1];
                          
                          return (
                            <div
                              key={`${doc}-${index}`}
                              className={`w-full flex items-center p-2 rounded-lg ${colors.bg} border ${colors.border}`}
                            >
                              <div className="flex-shrink-0">
                                <DocumentIcon className={`h-5 w-5 ${colors.icon}`} />
                              </div>
                              <div className="ml-3 flex-1">
                                <p className={`text-sm font-medium ${colors.text}`}>
                                  {docName}
                                </p>
                                {guarantorName && (
                                  <p className={`text-xs mt-0.5 ${colors.text} opacity-75`}>
                                    For guarantor: {guarantorName}
                                  </p>
                                )}
                              </div>
                              <div className="ml-2">
                                <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}>
                                  Required
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Loan Application Section */}
                  {!formData.loanApplicationComplete && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2">
                        Loan Application
                      </h4>
                      <button
                        onClick={handleLoanApplicationClick}
                        className="w-full flex items-center p-2 rounded-lg bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors"
                      >
                        <div className="flex-shrink-0">
                          <DocumentTextIcon className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3 flex-1 text-left">
                          <p className="text-sm font-medium text-blue-700">
                            Complete Loan Application
                          </p>
                          <p className="text-xs mt-0.5 text-blue-600 opacity-75">
                            {formData?.loanSize ? 'Review application details' : 'Start your application'}
                          </p>
                        </div>
                        <div className="ml-2">
                          <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700">
                            Required
                          </span>
                        </div>
                      </button>
                    </div>
                  )}

                  {/* Business Documents Section */}
                  {groupDocuments(getNextRequiredDocuments()).business.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2">
                        Business Documents
                      </h4>
                      <div className="space-y-2">
                        {groupDocuments(getNextRequiredDocuments()).business.map((doc, index) => {
                          const colors = getStatusColor(doc);
                          const docName = doc.split(' - ')[0];
                          
                          return (
                            <div
                              key={`${doc}-${index}`}
                              className={`w-full flex items-center p-2 rounded-lg ${colors.bg} border ${colors.border}`}
                            >
                              <div className="flex-shrink-0">
                                <DocumentIcon className={`h-5 w-5 ${colors.icon}`} />
                              </div>
                              <div className="ml-3 flex-1">
                                <p className={`text-sm font-medium ${colors.text}`}>
                                  {docName}
                                </p>
                                {doc.split(' - ')[1] && (
                                  <p className={`text-xs mt-0.5 ${colors.text} opacity-75`}>
                                    {doc.split(' - ')[1]}
                                  </p>
                                )}
                              </div>
                              <div className="ml-2">
                                <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}>
                                  Required
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
      )}

      {/* Group upload modal */}
      {bulkQueue.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Tag {bulkQueue.length} {bulkQueue.length === 1 ? 'file' : 'files'}</h3>
                <p className="text-xs text-gray-500 mt-0.5">We tried to guess each doc type from the filename — fix any that look wrong.</p>
              </div>
              <button onClick={closeBulkModal} disabled={bulkUploading} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Target business picker */}
            {businesses.filter(b => b.businessName).length > 0 && (
              <div className="px-6 py-3 border-b bg-gray-50">
                <label className="block text-xs font-medium text-gray-700 mb-1">Upload these files for</label>
                <select
                  value={bulkTargetBusinessIndex}
                  onChange={(e) => setBulkTargetBusinessIndex(e.target.value)}
                  disabled={bulkUploading}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                >
                  {businesses.filter(b => b.businessName).map(b => (
                    <option key={b.index} value={String(b.index)}>{b.businessName}</option>
                  ))}
                  <option value="">— Unassigned (general) —</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Files tagged to a business show as uploaded in that business's checklist.</p>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {bulkQueue.map((row, idx) => (
                <div key={idx} className={`border rounded-lg p-3 ${
                  row.status === 'done' ? 'border-green-200 bg-green-50' :
                  row.status === 'error' ? 'border-red-200 bg-red-50' :
                  row.status === 'uploading' ? 'border-indigo-200 bg-indigo-50' :
                  'border-gray-200'
                }`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <DocumentIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800 truncate">{row.file.name}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">{(row.file.size / 1024).toFixed(0)} KB</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {row.status === 'done' && <CheckCircleIcon className="h-5 w-5 text-green-500" />}
                      {row.status === 'uploading' && <span className="text-xs text-indigo-600 animate-pulse">Uploading…</span>}
                      {row.status !== 'done' && row.status !== 'uploading' && (
                        <button onClick={() => removeBulkRow(idx)} disabled={bulkUploading} className="text-gray-400 hover:text-red-500 disabled:opacity-30">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <select
                    value={row.label}
                    onChange={(e) => updateBulkRow(idx, { label: e.target.value })}
                    disabled={bulkUploading || row.status === 'done' || !!row.customLabel}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                  >
                    <option value="">Select document type…</option>
                    {GROUP_UPLOAD_DOCS.map(cat => (
                      <optgroup key={cat.group} label={cat.group}>
                        {cat.items.map(item => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={row.customLabel}
                    onChange={(e) => updateBulkRow(idx, { customLabel: e.target.value })}
                    placeholder="…or type a custom name (overrides above)"
                    disabled={bulkUploading || row.status === 'done'}
                    className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                  />
                  {row.error && <p className="mt-2 text-xs text-red-600">{row.error}</p>}
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                {bulkQueue.filter(r => r.status === 'done').length} of {bulkQueue.length} uploaded
              </p>
              <div className="flex gap-2">
                <button onClick={closeBulkModal} disabled={bulkUploading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                  {bulkQueue.every(r => r.status === 'done') ? 'Done' : 'Cancel'}
                </button>
                <button onClick={startBulkUpload}
                  disabled={bulkUploading || bulkQueue.every(r => r.status === 'done')}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  {bulkUploading ? 'Uploading…' : 'Upload all'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
